import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { Box, Chip, Grid, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Ticket } from "../services/ticketService";

const DIFFICULTY_COLOR: Record<string, string> = {
  LOW: "#6B8248",
  MEDIUM: "#C48A2A",
  HIGH: "#B54A4A",
};

function benefitColor(score: number): string {
  if (score >= 70) return "#6B8248";
  if (score >= 40) return "#C48A2A";
  return "#B54A4A";
}

function Metric({
  icon,
  label,
  value,
  color,
  footer,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  color: string;
  footer?: ReactNode;
}) {
  return (
    <Paper sx={{ p: 2, height: "100%", borderLeft: `4px solid ${color}` }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: `${color}1F`,
            color,
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {footer}
    </Paper>
  );
}

export function TicketAnalysisPanel({ analysis }: { analysis: NonNullable<Ticket["analysis"]> }) {
  const { t } = useTranslation();
  const difficulty = (analysis.difficulty || "MEDIUM").toUpperCase();
  const diffColor = DIFFICULTY_COLOR[difficulty] || "#4F8F8B";
  const score = analysis.benefitScore ?? 0;
  const scoreColor = benefitColor(score);
  const hours = analysis.estimatedHours;
  const budget = analysis.aiBudgetUsd;

  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        {t("tickets.analysis")}
      </Typography>
      {analysis.targetProduct || analysis.inScope === false ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {analysis.targetProduct ? (
            <Chip
              size="small"
              label={`${t("tickets.targetProduct")}: ${analysis.targetProduct}`}
              sx={{ bgcolor: "rgba(79,143,139,0.12)", color: "#2D5F5C" }}
            />
          ) : null}
          {analysis.inScope === false ? (
            <Chip size="small" color="warning" label={t("tickets.outOfScope")} />
          ) : null}
        </Stack>
      ) : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Metric
            icon={<SpeedOutlinedIcon fontSize="small" />}
            label={t("tickets.difficulty")}
            value={t(`difficultyLevel.${difficulty}`, { defaultValue: difficulty })}
            color={diffColor}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Metric
            icon={<TrendingUpOutlinedIcon fontSize="small" />}
            label={t("tickets.benefit")}
            value={`${score}/100`}
            color={scoreColor}
            footer={
              <LinearProgress
                variant="determinate"
                value={score}
                sx={{
                  mt: 1.25,
                  height: 6,
                  borderRadius: 1,
                  bgcolor: `${scoreColor}22`,
                  "& .MuiLinearProgress-bar": { bgcolor: scoreColor, borderRadius: 1 },
                }}
              />
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Metric
            icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
            label={t("tickets.aiBudget")}
            value={budget == null ? "—" : `$${budget}`}
            color="#304153"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Metric
            icon={<AccessTimeOutlinedIcon fontSize="small" />}
            label={t("tickets.effort")}
            value={hours == null ? "—" : t("tickets.hours", { count: hours })}
            color="#4F8F8B"
          />
        </Grid>
      </Grid>
      {(analysis.impact || []).length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
            <HubOutlinedIcon fontSize="small" sx={{ color: "#4F8F8B" }} />
            <Typography variant="body2" color="text.secondary">
              {t("tickets.impact")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {(analysis.impact || []).map((item) => (
              <Chip key={item} size="small" label={item} sx={{ bgcolor: "rgba(79,143,139,0.12)", color: "#2D5F5C" }} />
            ))}
          </Stack>
        </Box>
      ) : null}
      {analysis.summary ? (
        <Typography sx={{ mt: 2, whiteSpace: "pre-wrap" }} color="text.primary">
          {analysis.summary}
        </Typography>
      ) : null}
    </Paper>
  );
}
